// Ground-transport supplier labels — the operator-facing name of whoever is
// driving the passenger.
//
// The wire carries the ADAPTER KEY, not a brand: `apps/transport` ships a
// deterministic in-process vendor under the key `mock` (and the BFF's in-memory
// board uses `MockRides` / `MockShuttles`), so an operator reading the desk saw
// the literal word "mock" where a supplier belongs. That reads as a defect on a
// demo and tells the operator nothing about who is coming.
//
// So the key is mapped to a plausible supplier with an explicit `(M)` marker:
// the screen looks like the real thing while never claiming a simulated ride was
// booked with a live vendor. Change the map — not the call sites — to re-brand.

/** Suffix marking a simulated supplier. Never dropped: the honesty of the screen
 *  depends on a simulated ride being visibly simulated. */
const SIMULATED = '(M)';

const PROVIDER_LABEL: Record<string, string> = {
  // Simulated adapters — `apps/transport` (`MOCK_VENDOR`) and the BFF fixtures.
  mock: `Uber ${SIMULATED}`,
  mockrides: `Uber ${SIMULATED}`,
  mockshuttles: `Transvip ${SIMULATED}`,
  // Live adapters (`TRANSPORT_ADAPTER_DEFAULT`), shown under their own brand.
  uber: 'Uber',
  // The vouchers app names itself `uber-vouchers` (its adapter key) — same brand.
  ubervouchers: 'Uber',
  cabify: 'Cabify',
};

/**
 * Display name for a transport supplier. An unknown key is passed through rather
 * than hidden — a vendor we have no mapping for is still better named than blank,
 * and it makes the missing entry obvious the first time it ships.
 */
export function providerLabel(providerName: string | null | undefined): string {
  if (!providerName) return 'Supplier not reported';
  const key = providerName.trim().toLowerCase().replace(/[\s_-]+/g, '');
  return PROVIDER_LABEL[key] ?? providerName;
}
